import React, { useRef, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { createSearchParams } from 'react-router-dom'
import { useWindowWidth } from 'spa/hooks/useWindowWidth'
import Session from 'javascripts/layouts/v2/Session'
import { useClickAway } from 'react-use'

import logo from 'images/common/logo.svg'
import pen from 'images/common/icon_pen.svg'
import iconUser from 'images/common/icon_user.svg'
import iconArrow from 'images/common/icon_arrow.svg'
import iconSearch from 'images/common/icon_search.svg'

interface StyledHeaderProps {
  session?: Session;
}

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 101;
  display: flex;
  align-items: center;
  background-color: #fff;
  box-shadow: 0 .125rem .25rem rgba(34, 34, 34, .1);
  height: 3rem;
  padding: 10px 20px;

  @media ${props => props.theme.media.lg} {
    height: 4.625rem;
  }
`

const Logo = styled.a`
  img {
    width: 120px;
  }

  @media ${props => props.theme.media.xl} {
    img {
      width: 160px;
    }
  }
`

const Nav = styled.ul`
  list-style-type: none;
  display: none;

  @media ${props => props.theme.media.lg} {
    display: flex;
    margin: 0;
    padding: 0;
  }
`

const NavList = styled.li`
  margin-left: 1.5rem;
`

const NavJobOffersList = styled.li`
  margin-left: 1.5rem;

  &::before {
    content: "";
    border: solid rgba(192, 196, 206, 1);
    border-width: 0 1px 0 0;
    margin-right: 1.5rem;
  }
`

const NavLink = styled.a`
  color: #333;
  text-decoration: none;
  font-size: .875rem;
  line-height: 1.5;
  font-weight: 700;

  &:hover {
    color: #333;
    opacity: 0.7;
    text-decoration: none;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  font-size: .875rem;
  line-height: 1.5;
  font-weight: 700;
  gap: 8px;
`

const RegisterButton = styled.a`
  background-color: ${props => props.theme.members.colors.orange._80};
  border: none;
  border-radius: 25px;
  color: #fff;
  cursor: pointer;
  font-size: .75rem;
  line-height: 1;
  width: 6rem;
  margin-right: .5rem;
  padding-top: .5rem;
  padding-bottom: .5rem;
  text-align: center;

  &:hover {
    color: #fff;
    opacity: 0.7;
    text-decoration: none;
  }

  @media ${props => props.theme.media.lg} {
    padding: 10px 20px;
    font-size: .875rem;
    margin-right: 1rem;
    width: auto;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`

const LoginButton = styled.a`
  color: rgb(8, 135, 85);
  font-size: .75rem;
  text-align: center;
  margin-right: .5rem;
  width: 4rem;

  &:hover {
    color: rgb(8,135,85);
    opacity: 0.7;
    text-decoration: none;
  }

  @media ${props => props.theme.media.lg} {
    font-size: .875rem;
    text-decoration: none;
    margin-right: 1rem;
    width: auto;
  }
`

const SpecialLinkArea = styled.div`
  background: rgba(82, 171, 136, 0.2);

  @media ${props => props.theme.media.lg} {
    text-align: center;
    padding: 1rem;

    &:hover {
      opacity: 0.7;
    }
  }
`

const SpecialLink = styled.a`
  color: rgb(8, 135, 85);
  text-decoration: none;

  &:hover {
    color: rgb(8, 135, 85);
    text-decoration: none;
  }
`

const WorkReportButton = styled.button<{ isPc: boolean }>`
  display: flex;
  align-items: center;
  background-color: white;
  border: none;
  color: ${props => props.theme.clients.colors.silver._100};
  cursor: pointer;
  line-height: 1.5;
  padding: .5rem;
  font-weight: 700;

  @media ${props => props.theme.media.lg} {
    font-size: .875rem;
  }

  &:hover, &.open {
    opacity: .7;
  }

  &::before {
    content: "";
    background-size: 1.25rem auto;
    background: url(${pen}) no-repeat 50%;
    color: ${props => props.theme.clients.colors.silver._70};
    height: 1.5rem;
    width: 1.5rem;
    margin-right: .25rem;

    @media ${props => props.theme.media.md} {
      margin-right: .5rem;
    }
  }

  ${({ isPc }) => isPc && css`
    &::after {
      content: "";
      background-size: 1.25rem auto;
      background: url(${iconArrow}) no-repeat 50%;
      height: 1.5rem;
      width: 1.5rem;
      margin-left: .25rem;
    }
  `}
`

const JobOffersLink = styled.a`
  display: flex;
  align-items: center;
  background-color: white;
  border: none;
  color: ${props => props.theme.clients.colors.silver._100};
  cursor: pointer;
  line-height: 1.5;
  padding: .5rem;
  font-weight: 700;

  @media ${props => props.theme.media.md} {
    display: none;
  }

  &:hover {
    opacity: .7;
  }

  &::before {
    content: "";
    background-size: 1.25rem auto;
    background: url(${iconSearch}) no-repeat 50%;
    height: 1.5rem;
    width: 1.5rem;
    margin-right: .25rem;
  }
`

const WorkerSettingsButton = styled.button<{ isPc: boolean }>`
  display: flex;
  align-items: center;
  background-color: white;
  border: none;
  cursor: pointer;
  line-height: 1.5;
  padding: .5rem;

  @media ${props => props.theme.media.lg} {
    font-weight: 700;
    font-size: .875rem;
  }

  &:hover, &.open {
    opacity: .7;
  }

  &::before {
    content: "";
    background-size: 1.25rem auto;
    background: url(${iconUser}) no-repeat 50%;
    height: 1.5rem;
    width: 1.5rem;
  }

  ${({ isPc }) => isPc && css`
    &::after {
      content: "";
      background-size: 1.25rem auto;
      background: url(${iconArrow}) no-repeat 50%;
      height: 1.5rem;
      width: 1.5rem;
    }
  `}
`

const MenuIcon = styled.div`
  display: none;
  flex-direction: column;
  cursor: pointer;

  span {
    height: 2px;
    width: 16px;
    background-color: ${props => props.theme.members.colors.silver._100};
    margin: 2px 0;
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    display: flex;
  }

  &:hover {
    opacity: .7;
  }
`

const BackgroundSlideIn = keyframes`
  from {
    background: rgba(34, 34, 34, 0);
  }
  to {
    background: rgba(34, 34, 34, 0.8);
  }
`

const BackgroundSlideOut = keyframes`
  from {
    background: rgba(34, 34, 34, 0.8);
  }
  to {
    background: rgba(34, 34, 34, 0);
  }
`

const Background = styled.div<{ isOpen: boolean }>`
  position: fixed;
  z-index: 999;
  cursor: pointer;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  animation: ${({ isOpen }) => (isOpen ? BackgroundSlideIn : BackgroundSlideOut)} 0.6s forwards;
`

const SidebarSlideIn = keyframes`
  from {
    right: calc(4rem - 100%);
  }
  to {
    right: 0;
  }
`

const SidebarSlideOut = keyframes`
  from {
    right: 0;
  }
  to {
    right: calc(4rem - 100%);
  }
`

const Sidebar = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  width: calc(100% - 4rem);
  height: 100%;
  background-color: #fff;
  z-index: 1000;
  padding-top: 1rem;
  animation: ${({ isOpen }) => (isOpen ? SidebarSlideIn : SidebarSlideOut)} 0.6s forwards;
`

const CloseButton = styled.div`
  cursor: pointer;
  color: white;
  font-size: 30px;
  z-index: 1000;
  display: inline-block;
  position: fixed;
  margin: 1rem 1rem 1rem 1.5rem;
  top: 0;
  left: 0;
`

const SidebarNav = styled.ul`
  font-weight: 700;
  list-style-type: none;
  margin-bottom: 1.5rem;
  padding-left: 0;
  font-size: .875rem;
  line-height: 1.5;
`

const SidebarNavList = styled.li`
  padding: .5rem 1rem .5rem 1rem;
`

const SidebarNavJobOffersList = styled.li`
  padding: .5rem 1rem .5rem 1rem;

  &::before {
    content: "";
    display: block;
    border: solid rgba(192, 196, 206, 1);
    border-width: 1px 0 0;
    margin: .5rem 0;
    opacity: .6;
  }
`

const SidebarNavLink = styled.a`
    color: #333;
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }
`

const SidebarButtonGroup = styled.div`
  display: flex;
  flex-flow: column;
  font-weight: 700;
  font-size: .875rem;
`

const SidebarRegisterButton = styled.button`
  background-color: ${props => props.theme.members.colors.orange._80};
  border: none;
  border-radius: 25px;
  color: #fff;
  margin-left: auto;
  margin-right: auto;
  font-size: .875rem;
  line-height: 3rem;
  width: 100%;
  max-width: 20rem;
  margin-top: 1rem;

  &:hover {
    opacity: 0.7;
  }
`

const SidebarLoginButton = styled.a`
  color: rgb(8, 135, 85);
  font-size: .875rem;
  text-decoration: none;
  text-align: center;
  line-height: 3rem;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    color: rgb(8,135,85);
    opacity: 0.7;
    text-decoration: none;
  }
`

const SidebarSpecialLink = styled.a`
    margin-top: 1rem;
    text-align: center;
    background-color: rgba(82, 171, 136, 0.2);
    color: rgba(8, 135, 85, 1);
    padding: 1rem;

    &:hover {
      color: rgba(8, 135, 85, 1);
      text-decoration: none;
    }
`

const headerDropDownTop = 'calc(3rem + 4px)'
const highHeaderDropDownTop = 'calc(4.625rem + 4px)'

const WorkReportMenuContainer = styled.div`
  position: fixed;
  top: ${headerDropDownTop};
  right: 65px;
  background-color: #fff;
  box-shadow: 0 .25rem .5rem rgba(34, 34, 34, .2);
  z-index: 1000;
  width: 20rem;

  @media ${props => props.theme.media.md} {
    top: ${highHeaderDropDownTop};
  }
`

const WorkReportMenuItem = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: rgba(34, 34, 34, 1);
  cursor: pointer;
  padding: .75rem .25rem .75rem 1rem;

  &:hover {
    background-color: rgba(247, 248, 250, 1);
    opacity: .7;
  }
`

const WorkReportMenuTitle = styled.div`
  line-height: 1.75;
`

const WorkReportMenuDate = styled.div`
  line-height: 1.75;
`

const WorkerSettingsMenuContainer = styled.div`
  position: fixed;
  top: ${headerDropDownTop};
  right: 20px;
  background-color: #fff;
  box-shadow: 0 .25rem .5rem rgba(34, 34, 34, .2);
  z-index: 1000;
  padding-top: 1.25rem;
  padding-bottom: .75rem;
  width: 16rem;
  max-height: 350px;
  overflow-y: scroll;

  @media ${props => props.theme.media.md} {
    max-height: none;
    overflow: unset;

    top: ${highHeaderDropDownTop};
  }
`

const WorkerSettingsMenuSection = styled.div`
  margin-top: .25rem;
`

const WorkerSettingsSectionTitle = styled.h3`
  color: #333;
  font-weight: 700;
  font-size: .75rem;
  line-height: 1.5;
  color: rgba(117, 117, 117, 1);
  padding: .25rem 1rem;
  margin-bottom: 0;
`

const WorkReportDivider = styled.hr`
  color: rgba(192, 196, 206, .6);
  margin: 0;
`

const WorkerSettingsMenuItem = styled.a`
  display: block;
  font-size: .875rem;
  color: #333;
  text-decoration: none;
  font-weight: 700;
  margin-top: .25rem;
  padding: .25rem .25rem .25rem 1rem;
  line-height: 1.5;

  &:hover {
    color: #333;
    text-decoration: none;
    background-color: rgba(247, 248, 250, 1);
    opacity: .7;
  }

  &.highlight {
    &::after {
      content: "";
      margin-left: .125rem;
      height: .375rem;
      width: .375rem;
      vertical-align: top;
      background-color: rgba(241, 82, 82, 1);
      display: inline-block;
      border-radius: 9999px;
    }
  }

  &.small {
    font-size: .75rem;
    margin-top: .25rem;
    font-weight: 400;
  }
`

const WorkerSettingsDivider = styled.hr`
  color: rgba(192, 196, 206, .6);
  margin: .75rem 1rem;
`

export const StyledHeader: React.FC<StyledHeaderProps> = (props: StyledHeaderProps) => {
  const user = props.session?.user
  const login = !!user
  const windowWidth = useWindowWidth()
  const [isOpen, setIsOpen] = useState<boolean>(null)
  const [isOpenWorkReports, setIsOpenWorkReports] = useState<boolean>(false)
  const [isOpenWorkerSettings, setIsOpenWorkerSettings] = useState<boolean>(false)

  const isPc = !navigator.userAgent.match(/iPhone|Android.+Mobile|iPad/)

  const toggleMenu = () => setIsOpen(!isOpen)

  const onClickWorkReports = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.button !== 0) {
      return
    }

    setIsOpenWorkReports(!isOpenWorkReports)
    setIsOpenWorkerSettings(false)
  }

  const onClickWorkerSettings = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.button !== 0) {
      return
    }

    setIsOpenWorkerSettings(!isOpenWorkerSettings)
    setIsOpenWorkReports(false)
  }

  const onClickRegister = () => {
    const currentJobOfferId = location.pathname.match(/job_offers\/(\d+)/)?.[1]
    const newUserPathParams = currentJobOfferId ? { job_offer_id: currentJobOfferId } : {}

    window.location.href = Object.keys(newUserPathParams).length > 0
      ? `/signup?${createSearchParams(newUserPathParams).toString()}`
      : '/signup'
  }

  const LogoutHeaderMenu = () => {
    return (
      <Nav>
        <NavList>
          <NavLink href="/#service-features">サービスの特徴</NavLink>
        </NavList>
        <NavList>
          <NavLink href="/#service-flow">ご利用の流れ</NavLink>
        </NavList>
        <NavList>
          <NavLink href="/#service-faq">よくある質問</NavLink>
        </NavList>
        <NavList>
          <NavLink href="/#service-info">お役立ち情報</NavLink>
        </NavList>
        <NavJobOffersList>
          <NavLink href="/job_offers">募集案件</NavLink>
        </NavJobOffersList>
      </Nav>
    )
  }

  const LoginHeaderMenu = () => {
    return (
      <Nav>
        <NavList>
          <NavLink href="/">ダッシュボード</NavLink>
        </NavList>
        <NavList>
          <NavLink href="/job_offers">募集案件</NavLink>
        </NavList>
        {props.session && (
          <NavList>
            <NavLink href="/recommended_job_offers">おすすめの案件特集</NavLink>
          </NavList>
        )}
      </Nav>
    )
  }

  const LogoutButtonGroup = () => {
    return (
      <ButtonGroup>
        <RegisterButton onClick={onClickRegister}>{windowWidth >= 1024 ? '無料登録する' : '無料登録'}</RegisterButton>
        <LoginButton href="/login">{windowWidth >= 1024 ? 'ログインする' : 'ログイン'}</LoginButton>
        {windowWidth >= 1024 ?
          <SpecialLinkArea>
            <SpecialLink href="https://agent.crowdworks.jp/">
              フリーランスを<br />お探しの企業の方
            </SpecialLink>
          </SpecialLinkArea> :
          <MenuIcon onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </MenuIcon>
        }
      </ButtonGroup>
    )
  }

  const LoginButtonGroup = () => {
    return (
      <ButtonGroup>
        {user?.projects.length > 0 ?
          <WorkReportButton className={isOpenWorkReports && 'open'} onClick={onClickWorkReports} isPc={useWindowWidth() >= 768}>
            作業報告書
          </WorkReportButton> :
          <JobOffersLink href="/job_offers">
            お仕事検索
          </JobOffersLink>
        }
        <WorkerSettingsButton className={isOpenWorkerSettings && 'open'} onClick={onClickWorkerSettings} isPc={useWindowWidth() >= 768} />
      </ButtonGroup>
    )
  }

  const WorkReports = () => {
    const workReports = useRef<HTMLDivElement>(null)

    const handleClickOutside = () => {
      setIsOpenWorkReports(false)
    }

    // clickを指定しないと、ボタンの方のonClickイベントが発火しなくなるため、クリックイベントを指定
    useClickAway(workReports, handleClickOutside, ['click'])

    return (
      <WorkReportMenuContainer ref={workReports}>
        {user?.projects.map((project, index) => (
          <div key={index}>
            <WorkReportMenuItem key={index} onClick={() => window.location.href = project.monthly_work_reports_url}>
              <WorkReportMenuTitle>{project.title}</WorkReportMenuTitle>
              <WorkReportMenuDate>{`${project.start_on}〜${project.end_on}`}</WorkReportMenuDate>
            </WorkReportMenuItem>
            {user?.projects.length - 1 !== index && <WorkReportDivider />}
          </div>
        ))}
      </WorkReportMenuContainer>
    )
  }

  const WorkerSettings = () => {
    const workerSettings = useRef<HTMLDivElement>(null)

    const handleClickOutside = () => {
      setIsOpenWorkerSettings(false)
    }

    // clickを指定しないと、ボタンの方のonClickイベントが発火しなくなるため、クリックイベントを指定
    useClickAway(workerSettings, handleClickOutside, ['click'])

    return (
      <WorkerSettingsMenuContainer ref={workerSettings}>
        {user?.identify_verification_required && isPc &&
          <>
            <WorkerSettingsMenuSection>
              <WorkerSettingsMenuItem href="/dashboard" className="highlight" onClick={() => document.cookie = 'dashboard_identify_modal_open=true;path=/;max-age=60'}>本人確認のお願い</WorkerSettingsMenuItem>
            </WorkerSettingsMenuSection>
            <WorkerSettingsDivider />
          </>
        }
        {
          user?.show_invoice_request_on_profile_and_menu &&
          <>
            <WorkerSettingsMenuSection>
              <WorkerSettingsMenuItem href="/profile_sheet/basic_profile" className="highlight" onClick={() => document.cookie = 'profile_sheet_invoice_input_modal_open=true;path=/;max-age=60'}>インボイス登録番号の入力</WorkerSettingsMenuItem>
            </WorkerSettingsMenuSection>
            <WorkerSettingsDivider />
          </>
        }
        {user?.has_published_projects &&
          <>
            <WorkerSettingsMenuSection>
              <WorkerSettingsSectionTitle>契約関連</WorkerSettingsSectionTitle>
              <WorkerSettingsMenuItem href="/projects">契約一覧</WorkerSettingsMenuItem>
              <WorkerSettingsMenuItem href="/payments">お支払い一覧</WorkerSettingsMenuItem>
              <WorkerSettingsMenuItem href="/bank_account">振込先銀行口座の登録</WorkerSettingsMenuItem>
              <WorkerSettingsMenuItem href="/contact">本人情報・緊急連絡先</WorkerSettingsMenuItem>
            </WorkerSettingsMenuSection>
            <WorkerSettingsDivider />
          </>
        }
        <WorkerSettingsMenuSection>
          <WorkerSettingsSectionTitle>お仕事探し便利機能</WorkerSettingsSectionTitle>
          {props.session && <WorkerSettingsMenuItem href="/recommended_job_offers">おすすめの案件特集</WorkerSettingsMenuItem>}
          <WorkerSettingsMenuItem href="/favorite_job_offers">気になったお仕事</WorkerSettingsMenuItem>
          <WorkerSettingsMenuItem href="/job_offers/application_histories">応募履歴</WorkerSettingsMenuItem>
          <WorkerSettingsMenuItem href="/searching_bookmarks">保存した条件</WorkerSettingsMenuItem>
        </WorkerSettingsMenuSection>
        <WorkerSettingsDivider />
        <WorkerSettingsMenuSection>
          <WorkerSettingsSectionTitle>プロフィール設定</WorkerSettingsSectionTitle>
          <WorkerSettingsMenuItem href="/profile_sheet">プロフィールシート</WorkerSettingsMenuItem>
          <WorkerSettingsMenuItem href="/account">アカウント設定</WorkerSettingsMenuItem>
        </WorkerSettingsMenuSection>
        <WorkerSettingsDivider />
        <WorkerSettingsMenuSection>
          <WorkerSettingsMenuItem href="/faq" className="small">よくある質問</WorkerSettingsMenuItem>
          <WorkerSettingsMenuItem href="/inquiry" className="small">お問い合わせ</WorkerSettingsMenuItem>
          <WorkerSettingsMenuItem href="/logout" className="small">ログアウト</WorkerSettingsMenuItem>
        </WorkerSettingsMenuSection>
      </WorkerSettingsMenuContainer>
    )
  }

  return (
    <>
      <HeaderContainer>
        <Logo data-cy='job-offers-header-logo' href="/">
          <img src={logo} alt="クラウドワークス テック" />
        </Logo>
        {login ? <LoginHeaderMenu /> : <LogoutHeaderMenu />}
        {login ? <LoginButtonGroup /> : <LogoutButtonGroup />}
      </HeaderContainer>
      {isOpen !== null && windowWidth < 1024 &&
        <>
          <Background onClick={toggleMenu} isOpen={isOpen} />
          <Sidebar isOpen={isOpen}>
            {isOpen && <CloseButton onClick={toggleMenu}>×</CloseButton>}
            <SidebarNav>
              <SidebarNavList>
                <SidebarNavLink href="/#front-feature">サービスの特徴</SidebarNavLink>
              </SidebarNavList>
              <SidebarNavList>
                <SidebarNavLink href="/#front-usage">ご利用の流れ</SidebarNavLink>
              </SidebarNavList>
              <SidebarNavList>
                <SidebarNavLink href="/#front-faq">よくある質問</SidebarNavLink>
              </SidebarNavList>
              <SidebarNavList>
                <SidebarNavLink href="/#useful-info">お役立ち情報</SidebarNavLink>
              </SidebarNavList>
              <SidebarNavJobOffersList>
                <SidebarNavLink href="/job_offers">募集案件</SidebarNavLink>
              </SidebarNavJobOffersList>
            </SidebarNav>
            <SidebarButtonGroup>
              <SidebarRegisterButton onClick={onClickRegister}>無料登録</SidebarRegisterButton>
              <SidebarLoginButton href="/login">ログイン</SidebarLoginButton>
              <SidebarSpecialLink href="https://agent.crowdworks.jp/">
                フリーランスを<br />お探しの企業の方
              </SidebarSpecialLink>
            </SidebarButtonGroup>
          </Sidebar>
        </>
      }
      {isOpenWorkReports && <WorkReports />}
      {isOpenWorkerSettings && <WorkerSettings />}
    </>
  )
}
